import { useState, useEffect } from "react";
import SEO from "../components/SEO";

// ---------- COLOR TOKENS (matches site-wide black/white/gray palette) ----------
const C = {
  black: "#343F86",
  charcoal: "#343F86",
  graphite: "#262E63",
  steel: "#3A4060",
  ash: "#5B6072",
  silver: "#8A8F9C",
  smoke: "#CDD1E0",
  fog: "#E1E3EE",
  pearl: "#ECEEF6",
  white: "#F6F7FC",
  pure: "#FFFFFF",
};

const fontFamily = "'Inter', 'Montserrat', sans-serif";

// ---------- DATA ----------
// Placeholder legal content written to match a hospital design-build
// business. Have a lawyer review and adapt this before publishing —
// none of this is binding legal advice.
const SECTIONS = [
  {
    id: "scope-of-work",
    title: "1. Scope of Work",
    body: [
      "These Terms and Conditions (\"Terms\") govern every engagement between PR Hospital Designers Pvt. Ltd. (\"PR Hospital Designers\", \"we\", \"us\") and the client named in the applicable project agreement (\"Client\", \"you\").",
      "The scope of work for each project — including design, civil construction, MEP installation, medical gas systems, and NABH-compliance consulting — is defined in the signed project agreement or statement of work (\"SOW\"). These Terms apply in addition to, not instead of, that SOW.",
      "Any work not explicitly listed in the SOW is out of scope and will be handled as a Change Order under Section 4.",
    ],
  },
  {
    id: "payment-terms",
    title: "2. Payment Terms",
    body: [
      "Projects are billed in milestone-based installments as set out in the SOW — typically design sign-off, material procurement, execution start, and handover.",
      "Invoices are due within 15 days of issue unless otherwise agreed in writing. Late payments may accrue interest at 1.5% per month on the outstanding balance.",
      "Taxes, statutory levies, and government fees applicable to the project are billed separately unless the SOW states they are included.",
    ],
  },
  {
    id: "obligations",
    title: "3. Obligations of the Parties",
    body: [
      "PR Hospital Designers will execute the SOW in line with applicable building codes, NABH guidelines (where contracted), and the approved design drawings.",
      "The Client is responsible for providing timely site access, statutory approvals under their name (e.g. building permits, biomedical waste authorization), and prompt sign-off on design and material submittals.",
      "Delays caused by the Client's failure to provide access, approvals, or sign-offs do not count against PR Hospital Designers' delivery timeline under Section 5.",
    ],
  },
  {
    id: "change-orders",
    title: "4. Change Orders",
    body: [
      "Any modification to the approved scope — a layout change, material upgrade, or additional room — must be requested in writing and priced before work begins.",
      "Change orders may extend the project timeline. PR Hospital Designers will communicate the revised timeline before proceeding with the change.",
    ],
  },
  {
    id: "delivery-timelines",
    title: "5. Project Delivery Timelines",
    body: [
      "Estimated timelines are stated in the SOW and are based on uninterrupted site access and timely Client approvals.",
      "If PR Hospital Designers causes a delay beyond the agreed buffer period, a delay penalty as specified in the SOW applies. If the Client causes the delay, the timeline extends accordingly with no penalty to PR Hospital Designers.",
    ],
  },
  {
    id: "warranty",
    title: "6. Warranty",
    body: [
      "PR Hospital Designers provides a warranty on structural work, MEP installations, and modular OT/ICU systems as detailed in the project warranty document, typically covering defects in workmanship for a period stated in the SOW.",
      "The warranty does not cover damage from improper use, unauthorized modification, or lack of routine maintenance as outlined in the facility care instructions.",
    ],
  },
  {
    id: "liability",
    title: "7. Limitation of Liability",
    body: [
      "PR Hospital Designers' total liability for any claim arising from a project is limited to the total fees paid by the Client for that project.",
      "PR Hospital Designers is not liable for indirect, incidental, or consequential damages, including loss of revenue or patient volume, arising from project delays or defects, except where caused by gross negligence or willful misconduct.",
    ],
  },
  {
    id: "confidentiality",
    title: "8. Confidentiality & Intellectual Property",
    body: [
      "Design drawings, layouts, and technical documentation produced by PR Hospital Designers remain PR Hospital Designers' intellectual property until full payment is received, after which the Client receives a perpetual license to use them for the facility they were created for.",
      "Both parties agree to keep project-specific commercial terms and non-public facility information confidential, except where disclosure is required by law.",
    ],
  },
  {
    id: "termination",
    title: "9. Termination",
    body: [
      "Either party may terminate the agreement for material breach not cured within 30 days of written notice.",
      "Upon termination, the Client pays for all work completed and materials already procured up to the termination date.",
    ],
  },
  {
    id: "governing-law",
    title: "10. Governing Law & Dispute Resolution",
    body: [
      "These Terms are governed by the laws of India. Disputes will first be addressed through good-faith negotiation, escalating to arbitration in Hyderabad, Telangana under the Arbitration and Conciliation Act, 1996 if unresolved within 30 days.",
    ],
  },
  {
    id: "changes-to-terms",
    title: "11. Changes to These Terms",
    body: [
      "PR Hospital Designers may update these Terms from time to time. Material changes affecting an ongoing project will be communicated to the Client directly; continued engagement after notice constitutes acceptance.",
    ],
  },
];

// ---------- PAGE ----------
export default function TermsAndConditions() {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const [isMobile, setIsMobile] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  function scrollTo(id) {
    setActiveId(id);
    setNavOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
     <>
    <SEO/>
    <div className="terms-page">
      <style>{`
        .terms-page {
          min-height: 100vh;
          background: ${C.pure};
          color: ${C.black};
          font-family: ${fontFamily};
          padding-top: 68px;
        }
        @media (max-width: 900px) { .terms-page { padding-top: 60px; } }
        @media (max-width: 480px) { .terms-page { padding-top: 56px; } }

        .terms-section {
          scroll-margin-top: 100px;
        }
        @media (max-width: 900px) { .terms-section { scroll-margin-top: 76px; } }
        @media (max-width: 480px) { .terms-section { scroll-margin-top: 72px; } }
      `}</style>

      {/* ===== HERO (dark, matches site-wide hero style) ===== */}
      <section
        style={{
          background: C.black,
          padding: isMobile ? "48px 20px" : "72px 48px 44px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute", inset: 0,
            backgroundImage: `linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)`,
            backgroundSize: "48px 48px",
          }}
        />
        <div style={{ position: "relative", zIndex: 2, maxWidth: 1200, margin: "0 auto" }}>
          <p
            style={{
              fontFamily: fontFamily,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "3px",
              color: C.smoke,
              textTransform: "uppercase",
              background: "rgba(255,255,255,0.08)",
              display: "inline-block",
              padding: "6px 14px",
              borderRadius: 4,
              margin: isMobile ? "0 0 14px" : "0 0 18px",
            }}
          >
            Legal · PR Hospital Designers Hospital Infrastructure
          </p>
          <h1
            style={{
              fontFamily: fontFamily,
              fontWeight: 700,
              fontSize: isMobile ? 32 : "clamp(32px, 5vw, 56px)",
              margin: isMobile ? "0 0 12px" : "0 0 16px",
              letterSpacing: "-0.01em",
              maxWidth: 760,
              lineHeight: 1.15,
              color: C.pure,
            }}
          >
            Terms &amp; Conditions
          </h1>
          {/* <p style={{ fontFamily: fontFamily, fontSize: 12, color: C.silver, margin: 0 }}>
            Last updated: 06 July 2026
          </p> */}
        </div>
      </section>

      {/* ===== BODY ===== */}
      <section style={{ padding: isMobile ? "32px 20px 56px" : "56px 48px 80px", background: C.pure }}>
        <div
          style={{
            maxWidth: 1200, margin: "0 auto",
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "260px 1fr",
            gap: isMobile ? 20 : 40,
            alignItems: "start",
          }}
        >
          {/* Section nav — sticky sidebar on desktop, collapsible on mobile */}
          {isMobile ? (
            <div
              style={{
                border: `1px solid ${C.fog}`,
                borderRadius: 12,
                background: C.pearl,
                overflow: "hidden",
              }}
            >
              <button
                onClick={() => setNavOpen((v) => !v)}
                style={{
                  width: "100%",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  background: "none", border: "none", cursor: "pointer",
                  padding: "14px 16px",
                  fontFamily: fontFamily, fontSize: 13, fontWeight: 600, color: C.black,
                }}
              >
                On this page
                <span style={{ fontSize: 16, color: C.ash, transform: navOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▾</span>
              </button>
              {navOpen && (
                <ul style={{ listStyle: "none", padding: "0 8px 8px", margin: 0, display: "grid", gap: 2 }}>
                  {SECTIONS.map((s) => (
                    <li key={s.id}>
                      <button
                        onClick={() => scrollTo(s.id)}
                        style={{
                          display: "block", width: "100%", textAlign: "left",
                          background: activeId === s.id ? C.pure : "none",
                          border: "none", cursor: "pointer",
                          padding: "9px 8px", borderRadius: 6,
                          fontFamily: fontFamily, fontSize: 13, lineHeight: 1.5,
                          color: activeId === s.id ? C.black : C.ash,
                          fontWeight: activeId === s.id ? 600 : 400,
                        }}
                      >
                        {s.title}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <nav
              style={{
                position: "sticky",
                top: 24,
                border: `1px solid ${C.fog}`,
                borderRadius: 12,
                background: C.pearl,
                padding: "18px 16px",
              }}
            >
              <p
                style={{
                  fontFamily: fontFamily,
                  fontSize: 11,
                  color: C.silver,
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  fontWeight: 600,
                  margin: "0 0 12px",
                }}
              >
                On this page
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 2 }}>
                {SECTIONS.map((s) => (
                  <li key={s.id}>
                    <button
                      onClick={() => scrollTo(s.id)}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        background: activeId === s.id ? C.pure : "none",
                        border: "none",
                        borderRadius: 6,
                        cursor: "pointer",
                        padding: "8px 8px",
                        fontFamily: fontFamily,
                        fontSize: 13,
                        lineHeight: 1.5,
                        color: activeId === s.id ? C.black : C.ash,
                        fontWeight: activeId === s.id ? 600 : 400,
                        transition: "background 0.15s, color 0.15s",
                      }}
                    >
                      {s.title}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          {/* Sections */}
          <div>
            {SECTIONS.map((s) => (
              <div key={s.id} id={s.id} className="terms-section" style={{ marginBottom: isMobile ? 28 : 40, paddingBottom: isMobile ? 22 : 32, borderBottom: `1px solid ${C.fog}` }}>
                <h2 style={{ fontFamily: fontFamily, fontSize: isMobile ? 18 : 20, fontWeight: 700, margin: "0 0 14px", color: C.black }}>
                  {s.title}
                </h2>
                {s.body.map((para, i) => (
                  <p key={i} style={{ fontSize: isMobile ? 14 : 15, color: C.steel, lineHeight: 1.75, margin: "0 0 14px" }}>
                    {para}
                  </p>
                ))}
              </div>
            ))}

            <div style={{ border: `1px dashed ${C.fog}`, borderRadius: 10, padding: isMobile ? "16px 18px" : "20px 22px" }}>
              <p style={{ fontSize: 13, color: C.ash, margin: 0, lineHeight: 1.7 }}>
                Questions about these Terms? Reach out via our{" "}
                <a href="/contact-us" style={{ color: C.black, fontWeight: 600 }}>
                  Contact Us
                </a>{" "}
                page.
              </p>
            </div>
          </div>
        </div>
      </section>

     
    </div>
    </>
  );
}